import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { colors, LoadingSpinner, Card, EmptyState, Button } from '../../shared';
import { Lightbulb, RefreshCw, ChevronRight, MessageCircle, TrendingUp } from 'lucide-react';

const ThemeCard = ({ theme, onClick }) => (
  <Card
    className="cursor-pointer hover:border-opacity-50 transition-all mb-3"
    style={{ border: `1px solid ${colors.border}` }}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <h3 className="font-semibold mb-1" style={{ color: colors.textPrimary }}>
          {theme.name}
        </h3>
        <p className="text-sm mb-2" style={{ color: colors.textSecondary }}>
          {theme.description}
        </p>
        <div className="flex items-center gap-3 text-xs" style={{ color: colors.textMuted }}>
          <span className="flex items-center gap-1">
            <MessageCircle size={12} />
            {theme.count} occurrences
          </span>
          <span className="flex items-center gap-1">
            <TrendingUp size={12} />
            {theme.keywords?.slice(0, 3).join(', ')}
          </span>
        </div>
      </div>
      <ChevronRight size={18} style={{ color: colors.textMuted }} />
    </div>
  </Card>
);

const ThemeDetail = ({ theme, onBack }) => (
  <div>
    <button
      onClick={onBack}
      className="flex items-center gap-2 mb-4"
      style={{ color: colors.textMuted }}
    >
      <ChevronRight size={16} className="rotate-180" />
      Back to themes
    </button>

    <h2 className="text-xl font-bold mb-2" style={{ color: colors.textPrimary }}>
      {theme.name}
    </h2>
    <p className="mb-4" style={{ color: colors.textSecondary }}>
      {theme.description}
    </p>

    <div className="mb-6">
      <h3 className="text-sm font-medium mb-2" style={{ color: colors.textMuted }}>Keywords</h3>
      <div className="flex flex-wrap gap-2">
        {theme.keywords?.map((keyword, idx) => (
          <span
            key={idx}
            className="px-2 py-1 rounded text-sm"
            style={{ background: colors.yellowDim, color: colors.yellow }}
          >
            {keyword}
          </span>
        ))}
      </div>
    </div>

    <div>
      <h3 className="text-sm font-medium mb-3" style={{ color: colors.textMuted }}>
        Example Clarifications ({theme.examples?.length || 0})
      </h3>
      {theme.examples?.length > 0 ? (
        <div className="space-y-3">
          {theme.examples.map((example, idx) => (
            <Card key={idx}>
              <p className="text-sm mb-2" style={{ color: colors.textPrimary }}>
                "{example.text}"
              </p>
              <p className="text-xs" style={{ color: colors.textMuted }}>
                Estimate: {example.estimateId}
              </p>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-sm" style={{ color: colors.textMuted }}>No examples available</p>
      )}
    </div>
  </div>
);

export default function ClarityTab({ permissions }) {
  const [loading, setLoading] = useState(true);
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [clustering, setClustering] = useState(false);

  useEffect(() => {
    loadThemes();
  }, []);

  const loadThemes = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'clarity_themes'));
      const themesData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.count || 0) - (a.count || 0));
      setThemes(themesData);
    } catch (error) {
      console.error('Error loading themes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecluster = async () => {
    setClustering(true);
    try {
      // This would call a Cloud Function to re-run clustering
      // For now, just reload the themes
      await new Promise(resolve => setTimeout(resolve, 2000));
      await loadThemes();
    } catch (error) {
      console.error('Error re-clustering:', error);
    } finally {
      setClustering(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>Clarity Themes</h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Common clarification patterns from users
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={handleRecluster}
          disabled={clustering}
        >
          <RefreshCw size={16} className={clustering ? 'animate-spin' : ''} />
          {clustering ? 'Clustering...' : 'Re-cluster'}
        </Button>
      </div>

      {selectedTheme ? (
        <ThemeDetail
          theme={selectedTheme}
          onBack={() => setSelectedTheme(null)}
        />
      ) : (
        <>
          {themes.length === 0 ? (
            <EmptyState
              icon={Lightbulb}
              title="No themes yet"
              description="Clarity themes will be generated from user clarification patterns"
            />
          ) : (
            <div>
              {themes.map(theme => (
                <ThemeCard
                  key={theme.id}
                  theme={theme}
                  onClick={() => setSelectedTheme(theme)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
